import { ColoredBallPill } from 'component/colorpill';
import { PollPicker, PollResult } from 'component/poll';
import { RatingSelector } from 'component/ratingselector';
import type { CommentWithSlider } from 'feature/question/comment-slider';
import { HorizBarVisualization, Selector } from 'feature/question/comment-slider';
import { useState } from 'react';
import { View } from 'react-native-web';
import { CLICK, component, componentWithVariants, DemoBox, demoFile, extend, feature, story, variant } from 'system/demo';
import { SliderWithStopPoints } from 'system/sliderwithstoppoints';
import { t } from 'util/stdtypes';

demoFile('feature/question/comment-slider', {
    designUrl: 'https://www.figma.com/design/iP7DEaY8SVqQcEGN6Feipj/Basic-Profile-%2B-Privacy-Settings?node-id=475-5263&',
});

const optionLabels = [t('Strongly no'), t('No with reservations'), t("It's complicated"), t('Yes with reservations'), t('Strongly yes')];
const optionColors = ['red', 'orange', 'yellow', 'green', 'blue'];

function SliderWithStopPointsDemo() {
    const [value, setValue] = useState<number | null>(null);
    return (
        <DemoBox>
            <SliderWithStopPoints
                numberOfStops={5}
                onChangeValue={setValue}
                optionColors={optionColors}
                optionLabels={optionLabels}
                title='!No Default'
                value={value ?? 3}
            />
            <SliderWithStopPoints
                isDefault={!value}
                numberOfStops={5}
                onChangeValue={setValue}
                optionColors={optionColors}
                optionLabels={optionLabels}
                title='!Translucent Default'
                value={value ?? 3}
            />
            <SliderWithStopPoints
                numberOfStops={5}
                onChangeValue={setValue}
                optionColors={optionColors}
                optionLabels={optionLabels}
                title='!Default to Center'
                value={value ?? 3}
            />
        </DemoBox>
    );
}

component('SliderWithStopPoints', SliderWithStopPointsDemo);

function PollPickerDemo() {
    const [value, setValue] = useState(3);
    return (
        <PollPicker
            onChangeValue={setValue}
            optionColors={optionColors}
            optionLabels={optionLabels}
            placeholder={t('!Are cats cute?')}
            value={value}
        />
    );
}

component('PollPicker', PollPickerDemo, {}, [story('Strongly no', [CLICK('Change'), CLICK('Strongly no')])]);

function SelectorDemo() {
    const [comment, setComment] = useState<CommentWithSlider>({ text: '', time: 0, from: 'a', key: '1' });
    return <Selector comment={comment} setComment={setComment} />;
}

componentWithVariants(
    'Selector',
    SelectorDemo,
    {
        config: {
            commentSliderLabels: ['Strongly no', 'No with reservations', "It's complicated", 'Yes with reservations', 'Strongly yes'],
            commentSliderVisualization: HorizBarVisualization,
            commentSliderPrompt: 'Slide to respond:',
        },
        features: ['commentslider'],
        structureKey: 'question',
    },
    [
        variant('With Slider', {
            config: {
                commentSliderSelector: RatingSelector,
            },
        }),
    ],
);

const config = {
    commentSliderLabels: ['Strongly no', 'No with reservations', "It's complicated", 'Yes with reservations', 'Strongly yes'],
    commentAllowEmpty: true,
    commentSliderVisualization: HorizBarVisualization,
};

const commentsWithSlider = [
    { key: '1', from: 'a', text: 'I love this movie!', slider: 5, time: 0 },
    { key: '2', from: 'b', text: 'It sucks', slider: 1, time: 0 },
    { key: '3', from: 'c', text: 'The cinematics are awesome, but no plot', slider: 3, time: 0 },
    { key: '4', from: 'd', text: 'How did this get made', slider: 1, time: 0 },
];

component('HorizBarVisualization', HorizBarVisualization, {
    config,
    args: {
        commentsWithSlider,
    },
});

componentWithVariants('PollResult', PollResult, {}, [
    variant('Default', {
        args: {
            optionLabels,
            myVote: 'Strongly No',
            votes: ["It's complicated", 'Strongly yes', 'Strongly yes', 'No with reservations'],
        },
    }),
]);

function ColoredBallPillDemo() {
    return (
        <DemoBox>
            <ColoredBallPill color='green' text='Yes' />
            <ColoredBallPill color='red' text='No' />
            <ColoredBallPill color='yellow' text='Maybe' />
        </DemoBox>
    );
}

component('ColoredBallPill', ColoredBallPillDemo);

feature('commentslider', {
    structureKey: 'question',
    instanceKey: 'a',
    features: {
        commentslider: true,
        reactiontypes: false,
    },
    state: {
        globals: {
            name: 'Is Godzilla Good?',
            questionAuthor: 'a',
            questionTime: Date.now(),
        },
        collections: {
            comment: [
                { key: 1, from: 'a', slider: 1, text: 'Godzilla sucks' },
                { key: 2, from: 'b', slider: 5, text: 'Godilla rules' },
                { key: 3, from: 'c', slider: 3, text: 'Godzilla does not exist' },
            ],
        },
    },
});
